import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter } from '../../router/Router';
import {
  AlertCircle,
  Save,
  CheckCircle,
} from 'lucide-react';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import { validateEgyptianPhone } from '../../utils/errors';
import { AppShell } from '../common/AppShell';
import { bookingService } from '../../services/bookingService';
import { StudentTripStats } from '../../types';

// Phase 1 & 2 Feature Components
import { StudentDashboard } from './StudentDashboard';
import { UniversitySearch } from '../university/UniversitySearch';
import { TripListing } from '../trips/TripListing';
import { TripDetails } from '../trips/TripDetails';
import { SeatSelection } from '../seats/SeatSelection';
import { DigitalTicket } from '../ticket/DigitalTicket';
import { MyBookings } from '../booking/MyBookings';
import { PaymentCheckout } from '../payment/PaymentCheckout';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { DemandRequestForm } from '../demand/DemandRequestForm';

export const StudentArea: React.FC = () => {
  const { user, studentProfile, completeProfile, isLoading } = useAuth();
  const { currentPath, matchRoute } = useRouter();

  // Profile Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editFullName, setEditFullName] = useState(studentProfile?.fullName || '');
  const [editPhone, setEditPhone] = useState(studentProfile?.phoneNumber || '');
  const [editAddress, setEditAddress] = useState(studentProfile?.address || '');
  const [editGov, setEditGov] = useState(
    studentProfile?.universityGovernorate && studentProfile?.universityGovernorate !== 'الإسكندرية'
      ? studentProfile.universityGovernorate
      : 'مطروح'
  );
  const [editUniId, setEditUniId] = useState(studentProfile?.universityId || '');
  const [editCustomUniName, setEditCustomUniName] = useState(
    studentProfile?.universityId?.startsWith('uni_unlisted') || studentProfile?.universityId === 'other'
      ? studentProfile?.universityName || ''
      : ''
  );
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tripStats, setTripStats] = useState<StudentTripStats | null>(null);

  useEffect(() => {
    if (user?.uid) {
      bookingService.getStudentTripStats(user.uid).then(setTripStats).catch(console.warn);
    }
  }, [user?.uid]);

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);

    if (!editFullName.trim()) {
      setSaveError('الاسم بالكامل مطلوب.');
      return;
    }

    if (!editAddress.trim()) {
      setSaveError('عنوان السكن بالإسكندرية مطلوب.');
      return;
    }

    const cleanPhone = editPhone.replace(/[^0-9]/g, '');
    if (!cleanPhone || !validateEgyptianPhone(cleanPhone)) {
      setSaveError('رقم الهاتف المحمول إجباري ويجب أن يكون 11 رقماً مصرياً صحيحاً (يبدأ بـ 010 أو 011 أو 012 أو 015).');
      return;
    }

    if (editUniId === 'other' && !editCustomUniName.trim()) {
      setSaveError('يرجى كتابة اسم الجامعة أو الأكاديمية يدوياً.');
      return;
    }

    const selectedUni = SEED_UNIVERSITIES.find((u) => u.id === editUniId);
    const resolvedUniversityName =
      editUniId === 'other'
        ? editCustomUniName.trim()
        : selectedUni?.name || studentProfile?.universityName || 'جامعة غير محددة';

    const finalGov =
      editUniId === 'other'
        ? (editGov.trim() || 'الإسكندرية')
        : (selectedUni?.governorate || editGov.trim() || 'الإسكندرية');

    const success = await completeProfile({
      fullName: editFullName.trim(),
      phoneNumber: cleanPhone,
      address: editAddress.trim(),
      universityGovernorate: finalGov,
      universityId: editUniId === 'other' ? `uni_unlisted_${Date.now()}` : (editUniId || 'other'),
      universityName: resolvedUniversityName,
    });

    if (success) {
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 4000);
    }
  };

  // Route Pattern Matching
  const bookingMatch = matchRoute('/student/booking/:bookingId');
  const paymentMatch = matchRoute('/student/payment/:bookingId');
  const seatMatch = matchRoute('/student/trips/:tripId/seats');
  const tripDetailsMatch = matchRoute('/student/trips/:tripId');

  // Compute Page Title and Back Navigation for AppShell
  let pageTitle = '';
  let showBack = false;
  let backTo = '/student';

  if (currentPath === '/student/universities') {
    pageTitle = 'اختر جامعتك';
    showBack = true;
    backTo = '/student';
  } else if (currentPath === '/student/trips') {
    pageTitle = 'الرحلات المتاحة';
    showBack = true;
    backTo = '/student/universities';
  } else if (tripDetailsMatch.isMatch) {
    pageTitle = 'تفاصيل الرحلة';
    showBack = true;
    backTo = '/student/trips';
  } else if (seatMatch.isMatch) {
    pageTitle = 'اختيار المقعد';
    showBack = true;
    backTo = `/student/trips/${seatMatch.params.tripId}`;
  } else if (paymentMatch.isMatch) {
    pageTitle = 'سداد قيمة الحجز';
    showBack = true;
    backTo = '/student/bookings';
  } else if (bookingMatch.isMatch) {
    pageTitle = 'التذكرة الإلكترونية';
    showBack = true;
    backTo = '/student/bookings';
  } else if (currentPath === '/student/bookings') {
    pageTitle = 'رحلاتي وتذاكري';
    showBack = true;
    backTo = '/student';
  } else if (currentPath === '/student/notifications') {
    pageTitle = 'الإشعارات';
    showBack = true;
    backTo = '/student';
  } else if (currentPath === '/student/profile') {
    pageTitle = 'الملف الشخصي';
    showBack = true;
    backTo = '/student';
  } else if (currentPath === '/student/request-trip') {
    pageTitle = 'طلب رحلة جديدة';
    showBack = true;
    backTo = '/student';
  }

  return (
    <AppShell title={pageTitle} showBack={showBack} backTo={backTo}>
      {/* Success Alert */}
      {saveSuccess && (
        <div className="alert-success-banner" role="status" style={{ marginBottom: '1rem' }}>
          <CheckCircle size={20} />
          <span>تم تحديث بيانات الملف الشخصي بنجاح!</span>
        </div>
      )}

        {/* 1. Digital Ticket View: /student/booking/:bookingId */}
        {bookingMatch.isMatch ? (
          <DigitalTicket bookingId={bookingMatch.params.bookingId} />
        ) : /* 2. Payment Checkout View: /student/payment/:bookingId */
        paymentMatch.isMatch ? (
          <PaymentCheckout bookingId={paymentMatch.params.bookingId} />
        ) : /* 3. Notifications Center View: /student/notifications */
        currentPath === '/student/notifications' ? (
          <NotificationCenter />
        ) : /* 4. Seat Selection View: /student/trips/:tripId/seats */
        seatMatch.isMatch ? (
          <SeatSelection tripId={seatMatch.params.tripId} />
        ) : /* 5. Trip Details View: /student/trips/:tripId */
        tripDetailsMatch.isMatch ? (
          <TripDetails tripId={tripDetailsMatch.params.tripId} />
        ) : /* 6. Trip Listing View: /student/trips */
        currentPath === '/student/trips' ? (
          <TripListing />
        ) : /* 7. University Search View: /student/universities */
        currentPath === '/student/universities' ? (
          <UniversitySearch />
        ) : /* 8. My Bookings View: /student/bookings */
        currentPath === '/student/bookings' ? (
          <MyBookings />
        ) : /* 9. Demand Request View: /student/request-trip */
        currentPath === '/student/request-trip' ? (
          <DemandRequestForm />
        ) : /* 8. Student Profile View / Edit: /student/profile */
        currentPath === '/student/profile' ? (
          <div className="student-profile-view">
            <div className="overview-card">
              <div className="card-header-row">
                <h4>بيانات الملف الشخصي والإقامة</h4>
                {!isEditing && (
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setEditFullName(studentProfile?.fullName || '');
                      setEditPhone(studentProfile?.phoneNumber || '');
                      setEditAddress(studentProfile?.address || '');
                      setEditGov(studentProfile?.universityGovernorate || 'القاهرة');
                      setEditUniId(studentProfile?.universityId || '');
                      setIsEditing(true);
                    }}
                  >
                    تعديل البيانات
                  </button>
                )}
              </div>

              {saveError && (
                <div className="auth-error-banner" role="alert">
                  <AlertCircle size={18} />
                  <span>{saveError}</span>
                </div>
              )}

              {!isEditing ? (
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">معرّف الطالب (Firebase UID):</span>
                    <span className="info-value">
                      <code>{user?.uid}</code>
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">الاسم بالكامل:</span>
                    <span className="info-value font-bold">{studentProfile?.fullName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">البريد الإلكتروني الموثق:</span>
                    <span className="info-value">{studentProfile?.email}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">رقم الهاتف المحمول:</span>
                    <span className="info-value">{studentProfile?.phoneNumber || 'غير مسجل (اختياري)'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">عنوان الإقامة بالإسكندرية:</span>
                    <span className="info-value">{studentProfile?.address}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">محافظة الجامعة:</span>
                    <span className="info-value">{studentProfile?.universityGovernorate}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">الجامعة:</span>
                    <span className="info-value font-bold">{studentProfile?.universityName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">خط السير المعتمد:</span>
                    <span className="info-value font-bold text-[#E5232A]">
                      {studentProfile?.assignedRoute || `الموقف الجديد بمحرم بيك ◄ ${studentProfile?.universityName || 'الجامعة'}`}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">نقطة الانطلاق المحددة:</span>
                    <span className="info-value font-bold">
                      {studentProfile?.defaultPickupStation || 'الموقف الجديد بمحرم بيك'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">بيانات الاشتراك والرحلات:</span>
                    <div className="info-value">
                      <span
                        className="badge-active"
                        style={{
                          backgroundColor: tripStats?.isMultiTripSubscriber ? '#2563eb' : (tripStats?.totalBooked ? '#059669' : '#64748b'),
                          color: '#ffffff',
                          display: 'inline-block',
                          marginBottom: '4px',
                        }}
                      >
                        {tripStats?.isMultiTripSubscriber
                          ? `مشترك في باقة (${tripStats.totalBooked} رحلات)`
                          : (tripStats?.totalBooked ? 'حجز رحلات فردية' : 'لا توجد حجوزات مسجلة')}
                      </span>
                      {tripStats && tripStats.totalBooked > 0 && (
                        <div style={{ fontSize: '12px', color: '#334155', fontFamily: 'monospace', marginTop: '2px' }}>
                          <span>المحجوز: <strong style={{ color: '#2563eb' }}>{tripStats.totalBooked}</strong></span>
                          <span style={{ margin: '0 6px', color: '#cbd5e1' }}>|</span>
                          <span>المتبقي: <strong style={{ color: '#16a34a' }}>{tripStats.remaining}</strong></span>
                          <span style={{ margin: '0 6px', color: '#cbd5e1' }}>|</span>
                          <span>المكتمل: <strong style={{ color: '#64748b' }}>{tripStats.completed}</strong></span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-label">حالة الحساب:</span>
                    <span className="info-value">
                      <span className="badge-active">نشط ومفعل (ACTIVE)</span>
                    </span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSaveProfile} className="auth-form" noValidate>
                  <div className="form-group">
                    <label className="form-label">الاسم بالكامل</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFullName}
                      onChange={(e) => setEditFullName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">رقم الهاتف المحمول (اختياري)</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      placeholder="01012345678"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">عنوان السكن بالإسكندرية</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">محافظة الجامعة</label>
                    <select
                      className="form-select font-semibold"
                      value={editGov}
                      onChange={(e) => setEditGov(e.target.value)}
                      disabled={isLoading}
                    >
                      {[
                        'مطروح',
                        'البحيرة',
                        'المنوفية',
                        'الغربية',
                        'كفر الشيخ',
                        'الجيزة',
                        'القاهرة',
                        'الدقهلية',
                        'دمياط',
                        'القليوبية',
                        'الشرقية',
                        'محافظة أخرى',
                      ].map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">الجامعة المقيد بها</label>
                    <select
                      className="form-select"
                      value={editUniId}
                      onChange={(e) => setEditUniId(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="">-- اختر الجامعة --</option>
                      {SEED_UNIVERSITIES.filter((u) => u.governorate === editGov).map((uni) => (
                        <option key={uni.id} value={uni.id}>
                          {uni.name} ({uni.campus || uni.city || uni.governorate})
                        </option>
                      ))}
                      <option value="other" style={{ fontWeight: 'bold', color: '#E5232A' }}>
                        ✏️ أخرى / غير مدرجة بالقائمة (إدخال يدوي)
                      </option>
                    </select>

                    {editUniId === 'other' && (
                      <div className="mt-2.5 p-3 rounded-lg border border-rose-200 bg-rose-50/70">
                        <label className="text-xs font-bold text-slate-800 block mb-1">
                          اسم الجامعة أو الأكاديمية (إدخال يدوي) *
                        </label>
                        <input
                          type="text"
                          className="form-input bg-white text-xs"
                          placeholder="مثال: جامعة الجلالة، جامعة بدر..."
                          value={editCustomUniName}
                          onChange={(e) => setEditCustomUniName(e.target.value)}
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-buttons-row">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isLoading}
                    >
                      <Save size={16} />
                      <span>حفظ التعديلات</span>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setIsEditing(false)}
                      disabled={isLoading}
                    >
                      إلغاء
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : (
          /* 9. Default Student Home Dashboard: /student */
          <StudentDashboard />
        )}
    </AppShell>
  );
};
